import { ConditionalLink } from "components/conditionalLink";
import { AddTaskModal } from "components/crm/AddTaskModal";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import { Search } from "components/DesignSystem/Search/Search";
import Tab from "components/DesignSystem/Tab/Tab";
import { TASK_LIST_TAB_CLICK } from "constants/analyticsEvents";
import { ALL, ARCHIVED, COMPLETED, ONGOING } from "constants/task";
import { useAnalytics } from "hooks/useAnalytics";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import authContext from "jwt_context&axios/authContext";
import { useContext, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { closeTaskSlider, setTaskSliderHeight } from "store/slices/taskList";
import { RootState } from "store/store";
import { AccordionTable } from "./AccordionTable";
import { DashboardLayout } from "components/DashboardLayout";
import { usePageTitle } from "hooks/usePageTitle";
import { taskAlarmApis } from "store/apis/taskList";
import { useToast } from "hooks/useToast";
import { PermissionBasedView } from "components/PermissionBasedView/PermissionBasedView";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { Diamond } from "components/icons/Diamond";
import { FILINGS_PAGE } from "constants/subscriptionPermissionFeatures";
import { TaxSubscriptionModal } from "pages/Tax/TaxSubscriptionModal";
import { useModal } from "hooks/useModal";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { OldTaskListFilter } from "./OldTaskListFilter";

export const refreshTaskListFor30Seconds = (
  invalidateApi: () => void
): Promise<void> => {
  return new Promise((resolve) => {
    let count = 0;
    const interval = setInterval(() => {
      count += 2;
      if (count >= 30) {
        clearInterval(interval);
        resolve();
      }
      invalidateApi();
    }, 2000);
  });
};

export const OldTaskList = () => {
  usePageTitle("Filings");
  const { url } = useRouteMatch();
  const { trackEvent } = useAnalytics();
  const { update } = useUpdateQuery();
  const query = useQuery();
  const checkForTasksLaunch = query.get("check_for_tasks");
  const taskType = query.get("task");
  const search = query.get("search");
  const { authtoken } = useContext(authContext);
  const dispatch = useDispatch();
  const { email, name } = authtoken;
  const location = useLocation();
  const rRef = useRef(null);

  const { ongoing_count, count, completed_count, archived_count } = useSelector(
    (state: RootState) => state.taskList.taskCounts
  );
  const subscriptionModal = useModal();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const modalDescription =
    "Unlock access to all tax & compliance filings and more with Tax Standard Plan";

  const tabs = [
    { name: "Ongoing", value: ONGOING, taskCount: ongoing_count },
    {
      name: "Completed",
      value: COMPLETED,
      taskCount: completed_count,
    },
    { name: "Archived", value: ARCHIVED, taskCount: archived_count },
    { name: "All", value: ALL, taskCount: count },
  ];

  const handleTabChange = (value: string) => {
    update({ query: "task", value: value || null });
    trackEvent(TASK_LIST_TAB_CLICK, {
      screen: location.pathname,
      tab_name: value,
      email,
      name,
    });
    dispatch(closeTaskSlider());
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    update({ query: "search", value: value || null });
  };

  const {
    successToast,
    loading: loadingToast,
    dismiss: dismissToast,
  } = useToast();

  useEffect(() => {
    //@ts-ignore
    const yCoordinate = rRef.current.getBoundingClientRect();

    dispatch(setTaskSliderHeight(window.innerHeight - yCoordinate.y - 89));
  }, [window.innerHeight]);

  useEffect(() => {
    const startCheckingForTasks = async () => {
      const loadingtoastId = loadingToast({
        title: "Launching filings",
        message: "Your filings are being created, please wait.",
      });
      try {
        await refreshTaskListFor30Seconds(() =>
          dispatch(taskAlarmApis.util.invalidateTags(["TaskList"]))
        );

        update({
          query: "check_for_tasks",
          value: null,
        });

        successToast({
          title: "Filings from the cart have been launched.",
        });
      } catch (error) {
      } finally {
        dismissToast(loadingtoastId);
      }
    };

    if (checkForTasksLaunch) {
      startCheckingForTasks();
    }
  }, [checkForTasksLaunch]);

  return (
    <>
      <Tab.Root
        asChild
        defaultValue={ONGOING}
        onValueChange={handleTabChange}
        value={taskType || ONGOING}
      >
        <DashboardLayout
          ref={rRef}
          header={
            <Header
              v2
              title="Filings"
              right={
                <div className="t-flex t-gap-2">
                  <PermissionBasedView
                    route="/tax/filings"
                    feature={FILINGS_PAGE}
                    isNav
                    blockedUI={
                      <Button
                        onClick={() => subscriptionModal.open()}
                        size="small"
                        customType="primary"
                      >
                        <div className="t-flex t-items-center t-gap-1.5">
                          <Diamond />
                          Add Filing
                        </div>
                      </Button>
                    }
                  >
                    <ConditionalLink to={`${url}/addtask`}>
                      <Button size="small" customType="primary">
                        <i className="fas fa-plus t-mr-2" />
                        Add Filing
                      </Button>
                    </ConditionalLink>
                  </PermissionBasedView>
                </div>
              }
              bottom={
                <Tab.List>
                  {tabs.map(({ name, value, taskCount }) => (
                    <Tab.Trigger
                      value={value}
                      key={value}
                      suffix={taskCount?.toString()}
                    >
                      {name}
                    </Tab.Trigger>
                  ))}
                </Tab.List>
              }
            />
          }
        >
          <div className="t-flex t-justify-between t-pb-2">
            <OldTaskListFilter />
            <div className="t-w-1/3">
              <Search
                placeholder="Search..."
                onChange={handleChange}
                value={search || ""}
                block
              />
            </div>
          </div>
          <div>
            <Tab.Content value={ALL} className="t-h-full">
              <AccordionTable />
            </Tab.Content>
            <Tab.Content value={ONGOING} className="t-h-full">
              <AccordionTable />
            </Tab.Content>
            <Tab.Content value={COMPLETED} className="t-h-full">
              <AccordionTable />
            </Tab.Content>
            <Tab.Content value={ARCHIVED} className="t-h-full">
              <AccordionTable />
            </Tab.Content>
          </div>
          <TaxSubscriptionModal
            show={subscriptionModal.isOpen}
            onClose={() => subscriptionModal.close()}
            entityId={entityId || ""}
            groupId={groupId}
            description={
              <div className="t-pt-1 t-pb-4 t-text-subtext t-text-text-60">
                {modalDescription}
              </div>
            }
          />
        </DashboardLayout>
      </Tab.Root>
      <AddTaskModal />
    </>
  );
};
